import React, { useEffect, useState } from "react";
import AttachedPrescription from "../presecription/AttachedPrescription";
import { useSelector } from "react-redux";
import { apiGET, apiPUT } from "../../utilities/apiHelpers";
import { API_URL } from "../../config";
import ButtonWithLoader from "../Button/ButtonWithLoader";
import { toast } from "react-toastify";
import scrollToTop from "../../utilities/scrollToTop";

const ChooseMedicines = ({ setCurrentStep, stepperProgressCartData }) => {
    const [duration, setDuration] = useState("");
    const [unit, setUnit] = useState('Days');
    const [selectedOption, setSelectedOption] = useState('asPerPrescription');
    const [loading, setLoading] = useState(false);
    const userId = useSelector((state) => state.user?.userData?.id) || ""


    const maxLimits = {
        Days: 180,
        Weeks: 25,
        Months: 6
    };


    const handleDurationChange = async (e) => {
        const inputValue = e.target.value;

        if (inputValue === "") {
            setDuration("");
            return;
        }
        let value = Number(inputValue);

        // Determine dynamic limits based on the current unit
        let min = 1;
        let max = 180; // Default for Days

        if (unit === 'Weeks') max = 28;
        if (unit === 'Months') max = 6;

        // Show error if user types beyond limits, but clamp the value for state/API
        if (e.target.value !== "" && (value < min || value > max)) {
            toast.error(`Duration for ${unit} must be between ${min} and ${max}`);
            if (value < min) value = min;
            if (value > max) value = max;
        }

        setDuration(value);

        // Call API to update stepper progress
        const updateStepperProgressPayload = {
            prescriptionDuration: value,
        };

        try {
            const userStepperAddResponse = await apiPUT(
                `${API_URL}/v1/stepper-progress/update-stepper-progress/${userId}`,
                updateStepperProgressPayload
            );
            if (userStepperAddResponse.status) {
                await apiGET(`${API_URL}/v1/stepper-progress/user-stepper-progress/${userId}`);
            }
        } catch (error) {
            console.log("Error updating selected prescription", error);
        }
    };

    const handleUnitChange = async (e) => {
        const newUnit = e.target.value;
        setUnit(newUnit);

        // REVALIDATION: If they switch from Days(100) to Months, 100 is invalid.
        // We must clamp the existing duration to the new unit's max.
        let currentDuration = duration;
        let max = 180;
        if (newUnit === 'Weeks') max = 28;
        if (newUnit === 'Months') max = 6;

        if (currentDuration > max) {
            currentDuration = max;
            setDuration(max);
        } else if (currentDuration < 1 && currentDuration !== "") {
            currentDuration = 1;
            setDuration(1);
        }

        const updateStepperProgressPayload = {
            prescriptionDurationUnit: newUnit,
            prescriptionDuration: currentDuration // Send updated duration if it was clamped
        }

        try {
            const userStepperAddResponse = await apiPUT(
                `${API_URL}/v1/stepper-progress/update-stepper-progress/${userId}`,
                updateStepperProgressPayload
            );

            if (userStepperAddResponse.status) {
                await apiGET(
                    `${API_URL}/v1/stepper-progress/user-stepper-progress/${userId}`
                );
            }
        } catch (error) {
            console.log("Error updating selected prescription", error);
        }
    };

    const handleOptionChange = (option) => {
        setSelectedOption(option);

        // Reset duration to 0 when switching to "call" option
        if (option === 'call') {
            setDuration("");
        }
    };

    const handleNextStep = async () => {
        // Only validate duration if "asPerPrescription" is selected
        if (selectedOption === 'asPerPrescription' && duration <= 0) {
            toast.error('Please enter a valid duration');
            return; // Exit early to prevent API call
        }

        const updatePayload = {
            prescriptionType: selectedOption,
        };

        // Only include duration fields if "asPerPrescription" is selected
        if (selectedOption === 'asPerPrescription') {
            updatePayload.prescriptionDuration = duration;
            updatePayload.prescriptionDurationUnit = unit;
        } else if (selectedOption === 'call') {
            // When "Ask MEDiiAPP to call" is selected, set duration to 0
            updatePayload.prescriptionDuration = 0;
            updatePayload.prescriptionDurationUnit = unit; // Keep unit for consistency
        }

        try {
            setLoading(true);
            const userStepperAddResponse = await apiPUT(
                `${API_URL}/v1/stepper-progress/update-stepper-progress/${userId}`,
                updatePayload
            );
            console.log("userStepperAddResponse handleNextStep", userStepperAddResponse);
            if (userStepperAddResponse.status) {
                setLoading(false);
                setCurrentStep(); // Move to next step only on success
            } else {
                setLoading(false);
                toast.error('Failed to update. Please try again.');
            }
        } catch (error) {
            setLoading(false);
            console.log("Error updating selected prescription", error);
            toast.error('Error updating prescription. Please try again.');
        }
    };

    useEffect(() => {
        scrollToTop()
    }, [])

    return <>
        <div className="p-6 mt-4 bg-white shadow-lg rounded-lg mx-auto">
            <h2 className="text-lg font-semibold mb-4">Choose Your Medicines</h2>
            <div className="flex lg:flex-row flex-col justify-between">
                <div>
                    <div className="mb-4">
                        <label className="flex items-center mb-2">
                            <input
                                type="radio"
                                name="orderOption"
                                value="asPerPrescription"
                                checked={selectedOption === 'asPerPrescription'}
                                onChange={() => handleOptionChange('asPerPrescription')}
                                className="mr-2"
                            />
                            Order everything as per prescription
                        </label>
                        {selectedOption === 'asPerPrescription' && (
                            <div className="space-y-1">
                                <div className="lg:flex items-center space-x-2">
                                    <input
                                        type="number"
                                        value={duration}
                                        onChange={handleDurationChange}
                                        className="w-40 p-2 border rounded-[40px] font-[600] outline-none focus:ring-1 focus:ring-[#095D7E]"
                                        // DYNAMIC MAX: Changes based on unit
                                        max={maxLimits[unit]}
                                        min={0}
                                        placeholder="Dosage"
                                    />
                                    <select
                                        value={unit}
                                        onChange={handleUnitChange}
                                        className="p-2 border rounded-[40px] bg-[#095D7E] text-white cursor-pointer outline-none"
                                    >
                                        <option value="Days">Days</option>
                                        <option value="Weeks">Weeks</option>
                                        <option value="Months">Months</option>
                                    </select>
                                </div>

                                {/* DYNAMIC HINT: Shows Max: 180 Days, 25 Weeks, etc. */}
                                <p className="text-gray-500 text-[12px] font-semibold px-8">
                                    Max: {maxLimits[unit]} {unit}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="mb-6">
                        <label className="flex items-center mb-2">
                            <input
                                type="radio"
                                name="orderOption"
                                value="call"
                                checked={selectedOption === 'call'}
                                onChange={() => handleOptionChange('call')}
                                className="mr-2"
                            />
                            Ask MEDiiAPP to call
                        </label>
                    </div>
                    <ButtonWithLoader loading={loading} buttonText={"Next"} onClick={handleNextStep} width={"w-[100px]"} />
                </div>
                <div className="lg:w-2/5 lg:border-l-2">
                    <AttachedPrescription type="uploadPrescription" stepperProgressCartData={stepperProgressCartData} />
                </div>
            </div>
        </div>
    </>
}

export default ChooseMedicines;